'use client';

import React from 'react';

export interface MapPolylinePoint {
  x: number;
  y: number;
}

export interface MapPolylineProps extends Omit<React.SVGAttributes<SVGPolylineElement>, 'points'> {
  /** Sequence of vertices in svg-coordinate space. Fewer than 2 → no render. */
  points: MapPolylinePoint[];
  /** Stroke colour (CSS string). */
  color: string;
  /** Stroke width in svg units. Default 0.6. */
  width?: number;
  /** Render with a dashed pattern (useful for non-revenue / dead-head segments). */
  dashed?: boolean;
  /** Stroke opacity, 0..1. Default 0.85. */
  opacity?: number;
}

const DEFAULT_DASH = '1.6 1.6';

/**
 * SVG polyline primitive intended to live inside the consumer's map
 * canvas (`<svg viewBox="0 0 100 100">…</svg>` or any other coordinate
 * system). Renders nothing for fewer than two points so a single-stop
 * route never produces a stray dot.
 */
export function MapPolyline({
  points,
  color,
  width = 0.6,
  dashed = false,
  opacity = 0.85,
  ...rest
}: MapPolylineProps) {
  if (points.length < 2) return null;
  const flat = points.map((p) => `${p.x},${p.y}`).join(' ');
  return (
    <polyline
      points={flat}
      fill="none"
      stroke={color}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray={dashed ? DEFAULT_DASH : undefined}
      opacity={opacity}
      {...rest}
    />
  );
}
